import { env } from 'env';

export interface IMytaLease {
  leaseId: string;
  nodeId: string;
  slotIndex: number | string;
  streamerId: string;
  signallingUrl: string;
  playerUrl?: string;
  buildUri?: string;
  expiresAt?: string;
  ttlSec?: number;
}

export interface IAcquireLeaseProps {
  eventId?: string;
  userId?: string;
  buildUri?: string;
}

export interface ILeasePhase {
  phase?: string;
  etaSec?: number;
  retryAfterSec?: number;
  provisioning?: boolean;
  hint?: string;
}

export class NoFreeSlotError extends Error {
  details: ILeasePhase;

  constructor(details: ILeasePhase = {}) {
    super('no_free_slot');
    this.name = 'NoFreeSlotError';
    this.details = details;
  }
}

const storedLeaseKey = (eventId: string) => `myta:lease:${eventId}`;

export interface IStoredLease extends IMytaLease {
  eventId: string;
  storedAt: number;
}

export default class MytaStreamsService {
  static getLeaseApiUrl(): string {
    return (env.REACT_APP_MYTA_STREAMS_LEASE_API_URL || '')
      .trim()
      .replace(/\/+$/, '');
  }

  static getLeaseApiToken(): string {
    return (env.REACT_APP_MYTA_STREAMS_LEASE_API_TOKEN || '').trim();
  }

  static isLeaseApiConfigured(): boolean {
    return !!(this.getLeaseApiUrl() && this.getLeaseApiToken());
  }

  static isNativePixelStreamingEnabled(): boolean {
    return (
      (env.REACT_APP_FEATURE_NATIVE_PIXEL_STREAMING || '').trim() === 'true' &&
      this.isLeaseApiConfigured()
    );
  }

  private static headers(): Record<string, string> {
    return {
      Authorization: `Bearer ${this.getLeaseApiToken()}`,
      'content-type': 'application/json',
    };
  }

  static async acquireLease(props: IAcquireLeaseProps): Promise<IMytaLease> {
    const startedAt = performance.now();
    const response = await fetch(`${this.getLeaseApiUrl()}/v1/ec2/leases`, {
      method: 'POST',
      headers: this.headers(),
      body: JSON.stringify(props),
    });
    console.log(
      `MYTA_TIMING lease_post status=${response.status} serverMs=${Math.round(
        performance.now() - startedAt,
      )}`,
    );

    if (response.status === 409) {
      const details: ILeasePhase = await response.json().catch(() => ({}));
      throw new NoFreeSlotError(details);
    }

    if (!response.ok) {
      const body = await response.text().catch(() => '');
      throw new Error(`Lease request failed: ${response.status} ${body}`);
    }

    return response.json();
  }

  // Reload survival: the lease is stashed per-tab (sessionStorage) so an F5 rebinds to the SAME slot
  // instead of releasing it — a release sends the slot through a restart cycle and the returning user
  // pays 60-90 s (and the ladder may even start a whole node). Tab close never releases either; the
  // server-side lease TTL (~120 s) reclaims abandoned seats — that is its designed job.
  static saveStoredLease(eventId: string, lease: IMytaLease): void {
    try {
      const stored: IStoredLease = { ...lease, eventId, storedAt: Date.now() };
      sessionStorage.setItem(storedLeaseKey(eventId), JSON.stringify(stored));
    } catch {
      /* storage unavailable — rebind is best-effort */
    }
  }

  static loadStoredLease(eventId: string): IStoredLease | null {
    try {
      const raw = sessionStorage.getItem(storedLeaseKey(eventId));
      if (!raw) {
        return null;
      }
      const stored: IStoredLease = JSON.parse(raw);
      if (!stored.leaseId || stored.eventId !== eventId) {
        return null;
      }
      return stored;
    } catch {
      return null;
    }
  }

  static clearStoredLease(eventId: string): void {
    try {
      sessionStorage.removeItem(storedLeaseKey(eventId));
    } catch {
      /* ignore */
    }
  }

  static async releaseLease(
    leaseId: string,
    options: { keepalive?: boolean } = {},
  ): Promise<void> {
    try {
      await fetch(`${this.getLeaseApiUrl()}/v1/ec2/leases/${leaseId}`, {
        method: 'DELETE',
        headers: this.headers(),
        keepalive: options.keepalive,
      });
    } catch (e) {
      console.warn(`Failed to release myta lease ${leaseId}`, e);
    }
  }

  // `null` = the lease is definitively gone (server answered non-2xx); `'network_error'` = the
  // request itself failed and the lease may still be alive — callers deciding whether to DISCARD a
  // stored lease must only do so on the definitive answer.
  static async renewLease(
    leaseId: string,
  ): Promise<IMytaLease | null | 'network_error'> {
    try {
      const response = await fetch(
        `${this.getLeaseApiUrl()}/v1/ec2/leases/${leaseId}/renew`,
        {
          method: 'POST',
          headers: this.headers(),
        },
      );

      if (!response.ok) {
        return null;
      }

      return await response.json();
    } catch (e) {
      console.warn(`Failed to renew myta lease ${leaseId}`, e);
      return 'network_error';
    }
  }
}
